import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaPlus,
  FaExpand,
  FaCog,
  FaTrash,
  FaEdit,
  FaSync,
  FaArrowsAlt,
  FaCompress,
  FaArrowLeft,
  FaFilter,
  FaExternalLinkAlt,
  FaInfoCircle,
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import WidgetBuilder from './WidgetBuilder';
import ChartRenderer from '../reports/charts/ChartRenderer';
import { transformWidgetChartForDisplay } from '../reports/charts/transformWidgetChart';
import { getChartTypeDisplayLabel } from '../reports/charts/chartDisplayUtils';
import { splitGroupsFromDatasetMetadata } from '../../utils/reportChartData';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import useReportChartData from '../../hooks/useReportChartData';
import useWidgetChrome from '../../hooks/useWidgetChrome';
import { useReportCurrency } from '../reports/hooks/useReportCurrency';
import {
  transformDatasetToPreviewData,
  chartDatasetMissingDerivedLine,
} from '../../utils/reportChartData';
import { formatDateTime } from '../../utils/formatUtils';
import { resolveDashboardRunUserDisplayLabel } from '../../utils/dashboardRunUserLabel';
import ReportViewer from '../reports/ReportViewer';
import ReportRuntimeFilterModal from '../reports/ReportRuntimeFilterModal';
import { fetchReportFilterMeta } from '../../utils/reportRuntimeFilters';
import { normalizeReportFilterForApi } from '../../utils/reportFilterOperators';
import { applyReportFilterConditionChange } from '../../utils/reportFilterConditionUtils';
import { getFilterValueType } from '../../utils/filterFieldValueUtils';
import KpiWidget from './KpiWidget';
import {
  executeKpiWidget,
  executeKpiPreview,
  getKpiWidgetLabel,
  normalizeKpiWidgetConfig,
  extractKpiAppearanceFields,
  isKpiDatasetValid,
  isPreviewKpiWidget,
  kpiClientLog,
  shouldRefreshKpiCache,
} from '../../utils/kpiWidgetData';
import {
  getWidgetRefreshRateSeconds,
  hasWidgetCachedDataset,
  isWidgetCacheExpired,
} from '../../utils/widgetDatasetCache';
import {
  getRefreshableWidgetsSorted,
  refreshWidgetsSequentially,
} from '../../utils/widgetRefresh';
import { calculateWidgetResizeSpans } from '../../utils/widgetResize';
import DashboardSettingsModal from './DashboardSettingsModal';
import './css/DashboardCanvas.css';

const isValidImperativeRef = (ref) =>
  typeof ref === 'function' ||
  (ref != null && Object.prototype.hasOwnProperty.call(ref, 'current'));

/** Builder-only fields that must not be stored on canvas widget rows. */
const stripDashboardBuilderFields = (widget) => {
  const {
    selectedDashboard,
    selectedReport,
    selectedChart,
    previewData,
    groupingInfo,
    selectedFields,
    selectedGroups,
    selectedColumnGroups,
    summarizedFields,
    summarizedData,
    dataset,
    dashboard_description,
    dashboard_label,
    report_label,
    ...canvasWidget
  } = widget;
  return canvasWidget;
};

/**
 * Embeds a dashboard widget using a ref object (not a callback ref) so
 * useImperativeHandle on the nested DashboardCanvas can attach safely.
 */
const EmbeddedDashboardViewer = ({
  widgetId,
  dashboardId,
  showHeader,
  onRegisterRef,
}) => {
  const canvasRef = useRef(null);

  useLayoutEffect(() => {
    onRegisterRef(widgetId, canvasRef.current);
    return () => onRegisterRef(widgetId, null);
  });

  return (
    <div className="widget-dashboard-viewer">
      <DashboardCanvas
        ref={canvasRef}
        isRunMode={true}
        dashboardId={dashboardId}
        showHeader={showHeader}
        embedded={true}
      />
    </div>
  );
};

const DashboardCanvas = forwardRef(function DashboardCanvas(
  { isRunMode = false, dashboardId: propDashboardId, showHeader = true, embedded = false },
  ref
) {
  const { dashboardId: urlDashboardId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dashboardId = propDashboardId || urlDashboardId;
  const viewAsUserIdFromUrl = searchParams.get('viewAsUserId') || undefined;
  const [widgets, setWidgets] = useState([]);
  const [originalWidgets, setOriginalWidgets] = useState([]); // Track original widgets for deletion detection
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [widgetChartData, setWidgetChartData] = useState({});
  const [loadingWidgets, setLoadingWidgets] = useState({});
  const [saving, setSaving] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [runUserDisplayLabel, setRunUserDisplayLabel] = useState('');
  const [viewingWidget, setViewingWidget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [widgetToDelete, setWidgetToDelete] = useState(null);
  /** Per-widget report filter definitions (from report builder). */
  const [widgetFilterMeta, setWidgetFilterMeta] = useState({});
  /** Draft values while filter modal is open. */
  const [widgetFilterDraft, setWidgetFilterDraft] = useState({});
  /** Applied run-time filters (not persisted to widget cache). */
  const [widgetRuntimeFilters, setWidgetRuntimeFilters] = useState({});
  const [filterModalWidgetId, setFilterModalWidgetId] = useState(null);
  const [filterMetaLoading, setFilterMetaLoading] = useState(false);
  const [widgetKpiErrors, setWidgetKpiErrors] = useState({});
  const [showDashboardSettings, setShowDashboardSettings] = useState(false);
  const [refreshingAll, setRefreshingAll] = useState(false);

  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizePreview, setResizePreview] = useState(null);
  const [draggedWidget, setDraggedWidget] = useState(null); // Still needed for resize functionality
  const [resizeStartPosition, setResizeStartPosition] = useState({ x: 0, y: 0 });
  const resizeStartSpansRef = useRef({ column_span: 6, row_span: 4 });
  const canvasRef = useRef(null);
  const dashboardRunUserIdRef = useRef(null);
  const widgetsRef = useRef(widgets);
  widgetsRef.current = widgets;
  const embeddedDashboardRefs = useRef({});
  const registerEmbeddedDashboardRef = useCallback((widgetId, instance) => {
    if (instance) {
      embeddedDashboardRefs.current[widgetId] = instance;
    } else {
      delete embeddedDashboardRefs.current[widgetId];
    }
  }, []);
  const { loadReportChartData } = useReportChartData();
  const {
    reportConversionBaseCode,
    reportConversionLookup,
    multiCurrencyEnabled,
  } = useReportCurrency();
  const widgetChrome = useWidgetChrome(isRunMode);
  console.log('DashboardCanvas - isRunMode:', isRunMode);
  console.log('DashboardCanvas - dashboardId:', dashboardId);
  // Load dashboard details and widgets together
  const loadDashboardAndWidgets = useCallback(async () => {
    try {
      if (!dashboardId) {
        console.error('DashboardCanvas - No dashboardId provided');
        setError('No dashboard ID provided');
        setLoading(false);
        return;
      }

      const dashboardApi = new DataApi('dashboard');
      const viewAsQs =
        viewAsUserIdFromUrl != null && viewAsUserIdFromUrl !== ''
          ? `?viewAsUserId=${encodeURIComponent(viewAsUserIdFromUrl)}`
          : '';
      const response = await dashboardApi.get(
        `/${dashboardId}/widgets${viewAsQs}`
      );
      console.log('DashboardCanvas - Response:', response);
      if (response.data.success) {
        const dashboardWithWidgets = response.data.data;
        console.log('DashboardCanvas - Loaded dashboard with widgets:', dashboardWithWidgets);

        // Extract dashboard details
        const { widgets: widgetsData, ...dashboardDetails } = dashboardWithWidgets;
        dashboardRunUserIdRef.current = dashboardDetails.run_user_id || null;
        setDashboard(dashboardDetails);
        console.log('DashboardCanvas - Dashboard details:', dashboardDetails);
        console.log('DashboardCanvas - Dashboard name:', dashboardDetails.name);
        console.log('DashboardCanvas - Dashboard label:', dashboardDetails.label);
        console.log('DashboardCanvas - Dashboard description:', dashboardDetails.description);

        // Filter out empty or invalid widgets
        const validWidgets = widgetsData.filter(widget => {
          // Check if widget exists and has required fields
          if (!widget || !widget.id) return false;

          // Ensure widget has a meaningful name
          if (!widget.name || widget.name.trim() === '') return false;

          const widgetType = (widget.type || widget.widget_type || '').toLowerCase();
          const parentId = widget.parent_id || widget.report_id || null;

          // For chart/report/kpi widgets we require a parent report id
          if (
            (widgetType === 'chart' ||
              widgetType === 'report' ||
              widgetType === 'kpi' ||
              !widgetType) &&
            !parentId
          ) {
            return false;
          }

          // Additional checks for common invalid patterns
          if (widget.name.toLowerCase().includes('test') && !parentId) return false;
          if (widget.name.toLowerCase().includes('default') && !parentId) return false;
          if (widget.name.toLowerCase().includes('sample') && !parentId) return false;

          return true;
        });

        const normalizedWidgets = validWidgets.map((widget) => {
          const widgetType = (widget.type || widget.widget_type || '').toLowerCase();
          if (widgetType !== 'kpi') return widget;
          return {
            ...widget,
            widget_config: normalizeKpiWidgetConfig(widget.widget_config),
          };
        });

        console.log('DashboardCanvas - Valid widgets after filtering:', normalizedWidgets);
        setWidgets(normalizedWidgets);
        setOriginalWidgets(normalizedWidgets); // Store original widgets to detect deletions

        // Load chart data for each valid widget
        if (normalizedWidgets.length > 0) {
          loadWidgetChartData(normalizedWidgets);
        }
      } else {
        setError('Failed to load dashboard and widgets');
      }
    } catch (err) {
      console.error('Error loading dashboard and widgets:', err);
      const status = err?.response?.status;
      if (status === 404) {
        setError(
          embedded
            ? 'This dashboard no longer exists. Remove or replace this home widget.'
            : 'This dashboard no longer exists.'
        );
      } else {
        setError('Error loading dashboard and widgets. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [dashboardId, viewAsUserIdFromUrl, embedded]);

  useEffect(() => {
    if (dashboardId) {
      loadDashboardAndWidgets();
    }
  }, [dashboardId, viewAsUserIdFromUrl, loadDashboardAndWidgets]);

  /** Background refresh when widget cache TTL (refresh_rate) has elapsed. */
  useEffect(() => {
    if (!dashboardId || widgets.length === 0) {
      return undefined;
    }

    const refreshExpiredWidgets = () => {
      const list = widgetsRef.current || [];
      list.forEach((widget) => {
        if (!isWidgetCacheExpired(widget)) return;

        const normalizedType = (
          widget.chart_type ||
          widget.type ||
          ''
        ).toLowerCase();
        if (normalizedType === 'report' || normalizedType === 'dashboard') {
          return;
        }

        if (normalizedType === 'kpi') {
          loadKpiDataForWidget(widget, { forceRefresh: true });
          return;
        }

        const parentId = widget.parent_id || widget.report_id;
        if (!parentId) return;

        const cfg = widget.chart_config || {};
        const missingDerivedLine =
          widget.dataset &&
          chartDatasetMissingDerivedLine(widget.dataset, cfg);
        const isMatrixChartWidget =
          cfg.stackByField ||
          cfg.stack_by_field ||
          cfg.matrixChart ||
          cfg.matrix_chart;

        if (
          hasWidgetCachedDataset(widget) &&
          isDatasetStructureValid(widget.dataset) &&
          !isMatrixChartWidget &&
          !missingDerivedLine
        ) {
          loadChartDataForWidget(widget);
        } else if (!hasWidgetCachedDataset(widget)) {
          loadChartDataForWidget(widget);
        }
      });
    };

    const intervalMs = Math.min(
      60_000,
      Math.max(15_000, getWidgetRefreshRateSeconds(widgets[0]) * 1000)
    );
    const intervalId = setInterval(refreshExpiredWidgets, intervalMs);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh helpers read latest widgets via ref
  }, [dashboardId, widgets.length]);

  useEffect(() => {
    let cancelled = false;
    const runUserId = dashboard?.run_user_id;
    if (runUserId == null || String(runUserId).trim() === '') {
      setRunUserDisplayLabel('');
      return undefined;
    }
    (async () => {
      const label = await resolveDashboardRunUserDisplayLabel(runUserId);
      if (!cancelled) {
        setRunUserDisplayLabel(label);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dashboard?.run_user_id]);

  const loadWidgetChartData = async (widgetsData) => {
    console.log('DashboardCanvas - Loading chart data for widgets:', widgetsData);
    for (const widget of widgetsData) {
      const normalizedChartType = (widget.chart_type || widget.type || '').toLowerCase();
      if (normalizedChartType === 'report' || normalizedChartType === 'dashboard') {
        console.log('DashboardCanvas - Skipping chart data load for widget:', widget.id, 'type:', normalizedChartType);
        continue;
      }
      if (normalizedChartType === 'kpi') {
        kpiClientLog('loadWidgetChartData.kpiWidget', {
          id: widget.id,
          parent_id: widget.parent_id,
          widget_config: widget.widget_config,
        });
        const kpiCacheValid = widget.dataset && isKpiDatasetValid(widget.dataset);
        if (kpiCacheValid && !isWidgetCacheExpired(widget)) {
          kpiClientLog('loadWidgetChartData.kpiUsingCache', { id: widget.id });
          continue;
        }
        if (kpiCacheValid && isWidgetCacheExpired(widget)) {
          kpiClientLog('loadWidgetChartData.kpiCacheStale', { id: widget.id });
          loadKpiDataForWidget(widget, { forceRefresh: true });
          continue;
        }
        await loadKpiDataForWidget(widget);
        continue;
      }
      const parentId = widget.parent_id || widget.report_id || null;
      console.log('DashboardCanvas - Processing widget:', {
        id: widget.id,
        name: widget.name,
        parent_id: parentId,
        chart_type: widget.chart_type || (widget.chart_config && widget.chart_config.chartType),
        hasSavedDataset: !!widget.dataset,
        hasChartConfig: !!widget.chart_config
      });

      const shouldRefreshForLoggedInUserMismatch = (() => {
        if ((dashboard?.view_type || '').toUpperCase() !== 'LOGGED_IN_USER') {
          return false;
        }
        const currentRunUserId = dashboardRunUserIdRef.current || null;
        const savedRunUserId = widget?.dataset?._metadata?.run_user_id || null;
        if (!currentRunUserId) {
          return false;
        }
        // For logged-in-user dashboards, reuse cache only when the dataset was saved by same run user.
        return !savedRunUserId || savedRunUserId !== currentRunUserId;
      })();

      if (shouldRefreshForLoggedInUserMismatch) {
        console.log(
          'DashboardCanvas - Logged-in user changed since last saved dataset, refreshing widget:',
          widget.id
        );
        await loadChartDataForWidget(widget);
        continue;
      }

      const cfg = widget.chart_config || {};
      const isMatrixChartWidget =
        cfg.stackByField ||
        cfg.stack_by_field ||
        cfg.matrixChart ||
        cfg.matrix_chart;
      const missingDerivedLine =
        widget.dataset &&
        chartDatasetMissingDerivedLine(widget.dataset, cfg);
      const cacheExpired = isWidgetCacheExpired(widget);
      const hasCache = hasWidgetCachedDataset(widget);
      const cacheStructurallyValid =
        hasCache && isDatasetStructureValid(widget.dataset);
      const canUseCachedChart =
        cacheStructurallyValid && !isMatrixChartWidget && !missingDerivedLine;

      if (canUseCachedChart) {
        console.log('DashboardCanvas - Using cached dataset for widget:', widget.id, {
          cacheExpired,
        });
        await processCachedDataset(widget);
        if (cacheExpired) {
          console.log(
            'DashboardCanvas - Cache expired, refreshing widget in background:',
            widget.id
          );
          loadChartDataForWidget(widget);
        }
        continue;
      }

      if (hasCache && Object.keys(widget.dataset).length === 0) {
        console.log('DashboardCanvas - Widget has empty dataset, loading from API for widget:', widget.id);
        await loadChartDataForWidget(widget);
      } else if (parentId) {
        console.log('DashboardCanvas - No valid cache, loading from API for widget:', widget.id);
        await loadChartDataForWidget(widget);
      } else {
        console.log('DashboardCanvas - Widget has no parent/report, skipping:', widget.id);
      }
    }
  };

  // Helper function to check if cached dataset has valid structure for ChartDisplay
  const isDatasetStructureValid = (dataset) => {
    if (!dataset || !dataset._metadata) {
      console.log('DashboardCanvas - Dataset missing _metadata');
      return false;
    }

    const metadata = dataset._metadata;

    // Check if it has the required metadata structure
    if (!metadata.group_fields || !metadata.summarized_fields || !metadata.report_info) {
      console.log('DashboardCanvas - Dataset missing required metadata fields');
      return false;
    }

    // Check if it has the proper data structure that ChartDisplay expects
    const groupFields = Object.keys(metadata.group_fields);
    const summarizedFields = Object.keys(metadata.summarized_fields);

    if (groupFields.length === 0) {
      console.log('DashboardCanvas - Dataset has no group fields');
      return false;
    }

    // Check if the main data structure exists (e.g., "name" field with group values)
    const hasValidDataStructure = groupFields.some(groupField => {
      return dataset[groupField] && typeof dataset[groupField] === 'object';
    });

    if (!hasValidDataStructure) {
      console.log('DashboardCanvas - Dataset missing valid data structure for group fields');
      return false;
    }

    console.log('DashboardCanvas - Dataset structure is valid');
    return true;
  };

  // Helper function to process cached dataset and set up widget chart data
  const processCachedDataset = async (widget) => {
    try {
      console.log('DashboardCanvas - Processing cached dataset for widget:', widget.id);

      // Extract metadata and fields from the cached dataset
      const metadata = widget.dataset._metadata || {};
      const groupFields = metadata.group_fields || {};
      const summarizedFields = metadata.summarized_fields || {};

      // Transform dataset to previewData array format (same as before)
      const previewData = transformDatasetToPreviewData(widget.dataset);
      console.log('DashboardCanvas - Transformed previewData from cached dataset:', previewData);

      // Create field arrays in the format expected by ChartDisplay
      const selectedFields = Object.keys(summarizedFields).map(fieldName => {
        const fieldInfo = summarizedFields[fieldName];
        return {
          id: fieldInfo.field_id,
          field_name: fieldName,
          field_label: fieldInfo.field_label,
          data_type: fieldInfo.data_type,
          object_name: fieldInfo.object_name,
          is_aggregated: true,
          aggregate_function: fieldInfo.aggregation,
          is_grouped: false,
          sort_order: 1,
          summaryType: fieldInfo.aggregation
        };
      });

      // Always add record_count field if it exists in the cached data
      if (summarizedFields['record_count']) {
        selectedFields.push({
          id: 'record_count',
          field_name: 'record_count',
          field_label: 'Record Count',
          data_type: 'number',
          object_name: 'virtual',
          is_aggregated: true,
          aggregate_function: 'count',
          is_grouped: false,
          sort_order: 1,
          summaryType: 'count'
        });
      }

      const { rowGroups: selectedGroups, columnGroups: selectedColumnGroups } =
        splitGroupsFromDatasetMetadata(groupFields);

      // Create summarizedFields object in the format expected by ChartDisplay
      const processedSummarizedFields = {};
      Object.keys(summarizedFields).forEach(fieldName => {
        const fieldInfo = summarizedFields[fieldName];
        processedSummarizedFields[fieldInfo.field_id] = {
          aggregation: fieldInfo.aggregation
        };
      });

      // Also check if the chart configuration has summarizedFields (for record_count)
      if (widget.chart_config && widget.chart_config.summarizedFields) {
        console.log('DashboardCanvas - Found summarizedFields in chart_config:', widget.chart_config.summarizedFields);
        Object.keys(widget.chart_config.summarizedFields).forEach(fieldName => {
          const fieldInfo = widget.chart_config.summarizedFields[fieldName];
          // Use field_name as the key if field_id is not available (for virtual fields like record_count)
          const key = fieldInfo.field_id || fieldName;
          processedSummarizedFields[key] = {
            aggregation: fieldInfo.aggregation
          };
        });
      }

      // Set up widget chart data with cached dataset
      setWidgetChartData(prev => ({
        ...prev,
        [widget.id]: {
          previewData: previewData,
          summarizedData: widget.dataset,
          groupingInfo: {
            hasGrouping: true,
            groupedFields: selectedGroups,
            nonGroupedFields: selectedFields
          },
          selectedFields: selectedFields,
          summarizedFields: processedSummarizedFields,
          selectedGroups: selectedGroups,
          selectedColumnGroups: selectedColumnGroups
        }
      }));

      // Update the widget in state
      setWidgets(prev => prev.map(w =>
        w.id === widget.id ? { ...w, summarizedData: widget.dataset, dataset: widget.dataset } : w
      ));

      console.log('DashboardCanvas - Successfully processed cached dataset for widget:', widget.id);
    } catch (err) {
      console.error('DashboardCanvas - Error processing cached dataset:', err);
      // Fallback to loading fresh data if cached processing fails
      await loadChartDataForWidget(widget);
    }
  };

  // Function to update dashboard widget in the backend
  const updateDashboardWidgetInBackend = async (widgetId, updatedData) => {
    try {
      console.log('DashboardCanvas - Updating dashboard widget in backend:', widgetId, updatedData);

      const dashboardApi = new DataApi('dashboard');
      const response = await dashboardApi.put(`/widgets/${widgetId}`, updatedData);

      if (response?.data?.success) {
        console.log('DashboardCanvas - Successfully updated dashboard widget in backend:', widgetId);
        return response.data.data;
      } else {
        console.error('DashboardCanvas - Failed to update dashboard widget in backend:', response?.data?.message);
        throw new Error(response?.data?.message || 'Failed to update dashboard widget');
      }
    } catch (error) {
      console.error('DashboardCanvas - Error updating dashboard widget in backend:', error);
      // Don't throw the error to prevent breaking the main flow
      // The widget will still work with the updated data in memory
    }
  };

  const loadKpiDataForWidget = async (widget, options = {}) => {
    const {
      forceRefresh = false,
      filters: filtersOverride,
      persistToBackend = true,
    } = options;
    const runtimeFilters =
      filtersOverride !== undefined
        ? filtersOverride
        : widgetRuntimeFilters[widget?.id];
    const filtersParam = Array.isArray(runtimeFilters) ? runtimeFilters : [];
    const hasRuntimeFilters = filtersParam.length > 0;

    kpiClientLog('loadKpiDataForWidget.start', {
      widgetId: widget?.id,
      name: widget?.name,
      type: widget?.type,
      parent_id: widget?.parent_id,
      isPreview: widget?.isPreview,
      widget_config: widget?.widget_config,
      dataset: widget?.dataset,
      forceRefresh,
      runtimeFilterCount: filtersParam.length,
      persistToBackend,
    });

    if (!widget?.id) {
      kpiClientLog('loadKpiDataForWidget.skip', { reason: 'no-id' });
      return;
    }

    const parentId = widget.parent_id || widget.report_id;
    if (!parentId) {
      kpiClientLog('loadKpiDataForWidget.skip', { reason: 'no-parent_id' });
      return;
    }

    if (!widget.widget_config?.metric_field) {
      kpiClientLog('loadKpiDataForWidget.skip', {
        reason: 'no-metric_field-in-widget_config',
        widget_config: widget.widget_config,
      });
      setWidgetKpiErrors((prev) => ({
        ...prev,
        [widget.id]: 'Configure metric field in widget settings',
      }));
      return;
    }

    const isPreview = isPreviewKpiWidget(widget);

    const shouldRefresh =
      forceRefresh ||
      (!isPreview && shouldRefreshKpiCache(widget, dashboard, dashboardRunUserIdRef.current)) ||
      (!forceRefresh && isWidgetCacheExpired(widget));

    const cacheValid = widget.dataset && isKpiDatasetValid(widget.dataset);

    kpiClientLog('loadKpiDataForWidget.cacheCheck', {
      isPreview,
      shouldRefresh,
      cacheValid,
      cacheExpired: isWidgetCacheExpired(widget),
      datasetKeys: widget.dataset ? Object.keys(widget.dataset) : [],
    });

    if (!forceRefresh && !shouldRefresh && cacheValid && !hasRuntimeFilters) {
      kpiClientLog('loadKpiDataForWidget.usingCache', widget.dataset);
      setWidgetKpiErrors((prev) => {
        const next = { ...prev };
        delete next[widget.id];
        return next;
      });
      return;
    }

    try {
      setLoadingWidgets((prev) => ({ ...prev, [widget.id]: true }));
      setWidgetKpiErrors((prev) => {
        const next = { ...prev };
        delete next[widget.id];
        return next;
      });

      const result = isPreview
        ? await executeKpiPreview(dashboardId, {
          parentId,
          widgetConfig: widget.widget_config,
          viewAsUserId: viewAsUserIdFromUrl,
          forceRefresh,
          debug: true,
          filters: filtersParam,
        })
        : await executeKpiWidget(widget.id, {
          viewAsUserId: viewAsUserIdFromUrl,
          forceRefresh: forceRefresh || hasRuntimeFilters,
          debug: true,
          filters: filtersParam,
          skipCache: !persistToBackend || hasRuntimeFilters,
        });

      const rawDataset = result?.dataset || result?.widget?.dataset;
      const dataset = rawDataset
        ? {
          ...rawDataset,
          _metadata: {
            ...(rawDataset._metadata || {}),
            _runtime_filtered: hasRuntimeFilters,
            appearance: {
              ...extractKpiAppearanceFields(widget.widget_config),
              ...(rawDataset._metadata?.appearance || {}),
            },
          },
        }
        : null;

      kpiClientLog('loadKpiDataForWidget.result', {
        hasResult: !!result,
        hasDataset: !!dataset,
        value: dataset?.value,
        formatted_value: dataset?.formatted_value,
        debug: result?.debug,
      });

      if (dataset) {
        setWidgets((prev) =>
          prev.map((w) =>
            w.id === widget.id
              ? {
                ...w,
                dataset,
                refresh_datetime: new Date().toISOString(),
              }
              : w
          )
        );
      } else {
        kpiClientLog('loadKpiDataForWidget.noDatasetInResponse', result);
        setWidgetKpiErrors((prev) => ({
          ...prev,
          [widget.id]: 'KPI API returned no dataset',
        }));
      }
    } catch (err) {
      kpiClientLog('loadKpiDataForWidget.error', {
        message: err?.message,
        response: err?.response?.data,
      });
      setWidgetKpiErrors((prev) => ({
        ...prev,
        [widget.id]: err.message || 'Failed to load KPI',
      }));
    } finally {
      setLoadingWidgets((prev) => ({ ...prev, [widget.id]: false }));
    }
  };

  const loadChartDataForWidget = async (widget, options = {}) => {
    if (!widget) {
      console.warn('DashboardCanvas - Cannot load chart data, widget is undefined');
      return;
    }

    const parentId = widget.parent_id || widget.report_id;

    if (!parentId) {
      console.warn('DashboardCanvas - Cannot load chart data, missing parent/report ID for widget:', widget?.id);
      return;
    }

    const { filters: filtersOverride, persistToBackend = true } = options;
    const runtimeFilters =
      filtersOverride !== undefined
        ? filtersOverride
        : widgetRuntimeFilters[widget.id];
    const filtersParam = Array.isArray(runtimeFilters) ? runtimeFilters : [];

    try {
      console.log('DashboardCanvas - Loading chart data for widget:', widget.id, {
        persistToBackend,
        runtimeFilterCount: filtersParam.length,
      });
      setLoadingWidgets(prev => ({ ...prev, [widget.id]: true }));

      const result = await loadReportChartData({
        reportId: parentId,
        chartId: widget.chart_config?.id || widget.chart_id,
        existingChartConfig: widget.chart_config,
        limit: 100000,
        filters: filtersParam,
        runningUserId: dashboardRunUserIdRef.current || undefined,
        dashboardId: dashboardId || undefined,
        viewAsUserId: viewAsUserIdFromUrl,
        baseCurrencyCode: reportConversionBaseCode,
        exchangeRateLookup: reportConversionLookup,
        multiCurrencyEnabled,
      });

      const dataset = result.dataset || {};
      const datasetWithRunUser = {
        ...dataset,
        _metadata: {
          ...(dataset?._metadata || {}),
          run_user_id: dashboardRunUserIdRef.current || null,
          ...(persistToBackend
            ? {}
            : { _runtime_filtered: filtersParam.length > 0 }),
        },
      };
      const previewData = result.previewData || result.rawData || [];
      const chartConfig = result.chartConfig || widget.chart_config;

      setWidgetChartData(prev => ({
        ...prev,
        [widget.id]: {
          previewData,
          summarizedData: datasetWithRunUser,
          groupingInfo: result.groupingInfo,
          selectedFields: result.selectedFields || [],
          summarizedFields: result.summarizedFields || {},
          selectedGroups: result.selectedGroups || [],
          selectedColumnGroups: result.selectedColumnGroups || [],
          chartConfig,
        }
      }));

      if (persistToBackend) {
        setWidgets(prev => prev.map(w =>
          w.id === widget.id
            ? {
              ...w,
              dataset: datasetWithRunUser,
              summarizedData: datasetWithRunUser,
              selectedFields: result.selectedFields || [],
              summarizedFields: result.summarizedFields || {},
              selectedGroups: result.selectedGroups || [],
              selectedColumnGroups: result.selectedColumnGroups || [],
              chart_config: chartConfig,
              refresh_datetime: new Date().toISOString()
            }
            : w
        ));

        await updateDashboardWidgetInBackend(widget.id, {
          chart_config: chartConfig,
          dataset: datasetWithRunUser,
          refresh_datetime: new Date().toISOString()
        });
      }
    } catch (err) {
      console.error(`Error loading chart data for widget ${widget?.id}:`, err);
    } finally {
      setLoadingWidgets(prev => ({ ...prev, [widget?.id]: false }));
    }
  };

  const handleDeleteWidget = (widget) => {
    setWidgetToDelete(widget);
    setShowDeleteModal(true);
  };

  const confirmDeleteWidget = async () => {
    if (!widgetToDelete) return;

    try {
      const dashboardApi = new DataApi('dashboard');
      const response = await dashboardApi.delete(`/widgets/${widgetToDelete.id}`);

      if (response.data.success) {
        loadDashboardAndWidgets(); // Reload the list
      } else {
        setError('Failed to delete widget');
      }
    } catch (err) {
      console.error('Error deleting widget:', err);
      setError('Error deleting widget. Please try again.');
    } finally {
      setWidgetToDelete(null);
    }
  };


  const handleAddWidget = async (newWidget) => {
    console.log('DashboardCanvas - Received widget:', newWidget);
    console.log('DashboardCanvas - Widget column_span:', newWidget.column_span);

    // Set default column span to 6 (half of 12-column grid)
    const widgetTypeRaw = (newWidget.type || newWidget.chart_type || '').toLowerCase();
    const widgetWithDefaults = {
      ...newWidget,
      column_span: newWidget.column_span || 6,
      row_span: newWidget.row_span || 4,
      ...(widgetTypeRaw === 'kpi'
        ? { widget_config: normalizeKpiWidgetConfig(newWidget.widget_config) }
        : {}),
    };

    console.log('DashboardCanvas - Final widget column_span:', widgetWithDefaults.column_span);
    console.log('DashboardCanvas - Final widget data:', widgetWithDefaults);

    // Add the new widget to the list (now it's a preview)
    setWidgets(prev => [...prev, widgetWithDefaults]);
    setShowSidebar(false);

    const widgetType = (widgetWithDefaults.type || widgetWithDefaults.widget_type || '').toLowerCase();
    const parentId = widgetWithDefaults.parent_id || widgetWithDefaults.report_id;

    if (widgetWithDefaults.isPreview && widgetType === 'report') {
      setWidgetChartData(prev => {
        const updated = { ...prev };
        delete updated[widgetWithDefaults.id];
        return updated;
      });
    } else if (parentId && widgetType === 'chart') {
      await loadChartDataForWidget(widgetWithDefaults);
    } else if (parentId && widgetType === 'kpi') {
      await loadKpiDataForWidget(widgetWithDefaults);
    }
  };

  // Resize handlers
  const handleMouseDown = (e, widget, action) => {
    e.preventDefault();
    e.stopPropagation();

    if (action === 'resize') {
      // Get the handle from the current target (the resize handle div)
      const handle = e.currentTarget.dataset.handle || 'bottom-right';

      console.log('DashboardCanvas - Resize started:', {
        widgetId: widget.id,
        handle: handle,
        currentColumnSpan: widget.column_span,
        currentRowSpan: widget.row_span
      });
      setIsResizing(true);
      setResizeHandle(handle);
      setDraggedWidget(widget);
      resizeStartSpansRef.current = {
        column_span: widget.column_span || 6,
        row_span: widget.row_span || 4,
      };
      setResizeStartPosition({ x: e.clientX, y: e.clientY });

      // Add visual feedback
      document.body.style.cursor = getResizeCursor(handle);
      document.body.style.userSelect = 'none';

      // Add event listeners immediately
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isResizing && draggedWidget && resizeHandle) {
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasRect) return;

      const { column_span: newColumnSpan, row_span: newRowSpan } = calculateWidgetResizeSpans({
        resizeHandle,
        resizeStartPosition,
        startColumnSpan: resizeStartSpansRef.current.column_span,
        startRowSpan: resizeStartSpansRef.current.row_span,
        widgetX: draggedWidget.x || 0,
        clientX: e.clientX,
        clientY: e.clientY,
        canvasRect,
        maxRowSpan: 6,
      });

      // Update resize preview
      setResizePreview({ column_span: newColumnSpan, row_span: newRowSpan });

      console.log('DashboardCanvas - Resize calculation:', {
        widgetId: draggedWidget.id,
        handle: resizeHandle,
        newColumnSpan: newColumnSpan,
        newRowSpan: newRowSpan,
        oldColumnSpan: draggedWidget.column_span,
        oldRowSpan: draggedWidget.row_span
      });

      setWidgets(prev => prev.map(widget =>
        widget.id === draggedWidget.id
          ? { ...widget, column_span: newColumnSpan, row_span: newRowSpan }
          : widget
      ));
    }
  }, [isResizing, draggedWidget, resizeHandle, resizeStartPosition]);

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      console.log('DashboardCanvas - Resize ended:', {
        widgetId: draggedWidget?.id,
        finalColumnSpan: draggedWidget?.column_span,
        finalRowSpan: draggedWidget?.row_span
      });
    }

    // Remove event listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);

    setIsResizing(false);
    setDraggedWidget(null);
    setResizeHandle(null);
    setResizePreview(null);
    setResizeStartPosition({ x: 0, y: 0 });

    // Reset cursor and user selection
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [isResizing, draggedWidget]);

  // Helper function to get appropriate resize cursor
  const getResizeCursor = (handle) => {
    switch (handle) {
      case 'bottom-right':
        return 'se-resize';
      case 'bottom':
        return 's-resize';
      case 'right':
        return 'e-resize';
      default:
        return 'default';
    }
  };

  // Add event listeners for resize
  useEffect(() => {
    if (isResizing) {
      console.log('DashboardCanvas - Adding mouse event listeners');
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        console.log('DashboardCanvas - Removing mouse event listeners');
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  const handlePreviewWidget = (widgetConfig) => {
    // Handle widget preview/update
    console.log('DashboardCanvas - Adding/updating widget:', widgetConfig);
    console.log('DashboardCanvas - Chart configuration received:', {
      selectedFields: widgetConfig.selectedFields,
      summarizedFields: widgetConfig.summarizedFields,
      selectedGroups: widgetConfig.selectedGroups,
      previewData: widgetConfig.previewData,
      summarizedData: widgetConfig.summarizedData,
      selectedChart: widgetConfig.selectedChart,
      isPreview: widgetConfig.isPreview,
      id: widgetConfig.id
    });

    console.log('DashboardCanvas - Widget config details:', {
      hasPreviewData: !!widgetConfig.previewData,
      hasSummarizedData: !!widgetConfig.summarizedData,
      hasGroupingInfo: !!widgetConfig.groupingInfo,
      previewDataType: typeof widgetConfig.previewData,
      summarizedDataType: typeof widgetConfig.summarizedData,
      groupingInfoType: typeof widgetConfig.groupingInfo
    });

    const widgetTypeValue = (widgetConfig.type || widgetConfig.chart_type || '').toLowerCase();
    const withKpiConfig =
      widgetTypeValue === 'kpi'
        ? {
          ...widgetConfig,
          widget_config: normalizeKpiWidgetConfig(widgetConfig.widget_config),
        }
        : widgetConfig;

    if (widgetConfig.isPreview) {
      const widgetToAdd =
        widgetTypeValue === 'dashboard'
          ? stripDashboardBuilderFields(withKpiConfig)
          : withKpiConfig;
      setWidgets((prev) => [...prev, widgetToAdd]);
      if (widgetTypeValue === 'kpi' && (withKpiConfig.parent_id || withKpiConfig.report_id)) {
        loadKpiDataForWidget(withKpiConfig);
      }
    } else {
      // Update existing widget in the widgets state
      setWidgets(prev => prev.map(widget => {
        if (widget.id === withKpiConfig.id) {
          // Preserve existing important properties if widgetConfig doesn't have them
          const preservedSummarizedData = widgetConfig.summarizedData || widget.summarizedData;
          const preservedDataset = widgetConfig.dataset || widget.dataset;
          const preservedChartId = widgetConfig.chart_id || widget.chart_id;
          const preservedChartConfig = widgetConfig.chart_config || widget.selectedChart || widget.chart_config;
          const preservedParentId = widgetConfig.parent_id || widget.parent_id || widget.report_id || null;
          const normalizedType = widgetTypeValue || (widget.type || '').toLowerCase();

          console.log('DashboardCanvas - Updating widget with preserved data:', {
            widgetId: widget.id,
            widgetName: widget.name,
            hasOriginalSummarizedData: !!widget.summarizedData,
            hasOriginalDataset: !!widget.dataset,
            hasOriginalChartId: !!widget.chart_id,
            hasOriginalChartConfig: !!widget.chart_config,
            hasConfigSummarizedData: !!widgetConfig.summarizedData,
            hasConfigDataset: !!widgetConfig.dataset,
            hasConfigChartId: !!widgetConfig.chart_id,
            hasConfigChartConfig: !!widgetConfig.selectedChart,
            hasPreservedSummarizedData: !!preservedSummarizedData,
            hasPreservedDataset: !!preservedDataset,
            hasPreservedChartId: !!preservedChartId,
            hasPreservedChartConfig: !!preservedChartConfig,
            preservedParentId
          });

          return {
            ...withKpiConfig,
            summarizedData: normalizedType === 'report' ? null : preservedSummarizedData,
            dataset: normalizedType === 'report' ? null : preservedDataset,
            chart_id: normalizedType === 'report' ? null : preservedChartId,
            chart_config: (normalizedType === 'report' || normalizedType === 'dashboard') ? null : preservedChartConfig,
            parent_id: preservedParentId,
          };
        }
        return widget;
      }));
    }

    if ((widgetConfig.chart_type || widgetConfig.type) === 'report' || (widgetConfig.chart_type || widgetConfig.type) === 'dashboard') {
      setWidgetChartData(prev => {
        const updated = { ...prev };
        if (widgetConfig.id) {
          delete updated[widgetConfig.id];
        }
        return updated;
      });
    } else {
      const chartCfg = widgetConfig.chart_config || widgetConfig.selectedChart || {};
      const isMatrixChartWidget =
        chartCfg.stackByField ||
        chartCfg.stack_by_field ||
        chartCfg.matrixChart ||
        chartCfg.matrix_chart ||
        (widgetConfig.selectedColumnGroups?.length > 0);

      if (isMatrixChartWidget && (widgetConfig.parent_id || widgetConfig.report_id)) {
        loadChartDataForWidget(widgetConfig);
      } else if (widgetConfig.previewData && widgetConfig.summarizedData && widgetConfig.groupingInfo) {
        console.log('DashboardCanvas - Using existing chart data for widget:', widgetConfig.id);
        setWidgetChartData(prev => ({
          ...prev,
          [widgetConfig.id]: {
            previewData: widgetConfig.previewData,
            summarizedData: widgetConfig.summarizedData,
            groupingInfo: widgetConfig.groupingInfo,
            selectedFields: widgetConfig.selectedFields || [],
            summarizedFields: widgetConfig.summarizedFields || {},
            selectedGroups: widgetConfig.selectedGroups || [],
            selectedColumnGroups: widgetConfig.selectedColumnGroups || [],
            chartConfig: widgetConfig.chart_config || widgetConfig.selectedChart || null
          }
        }));
      } else {
        // Check if we already have chart data for this widget (preserve existing data)
        const existingChartData = widgetChartData[widgetConfig.id];
        if (existingChartData) {
          console.log('DashboardCanvas - Preserving existing chart data for updated widget:', widgetConfig.id);
          // Keep the existing chart data, just update the widget configuration
          setWidgetChartData(prev => ({
            ...prev,
            [widgetConfig.id]: {
              ...existingChartData,
              // Update any new configuration if provided
              selectedFields: widgetConfig.selectedFields || existingChartData.selectedFields,
              summarizedFields: widgetConfig.summarizedFields || existingChartData.summarizedFields,
              selectedGroups: widgetConfig.selectedGroups || existingChartData.selectedGroups,
              selectedColumnGroups:
                widgetConfig.selectedColumnGroups || existingChartData.selectedColumnGroups || []
            }
          }));
        } else if (widgetTypeValue !== 'dashboard' && widgetConfig.dataset) {
          // If widget has dataset from database, use it directly and standardize to summarizedData
          console.log('DashboardCanvas - Using dataset from database for updated widget:', widgetConfig.id);
          const metadata = widgetConfig.dataset._metadata || {};
          const groupFields = metadata.group_fields || {};
          const summarizedFields = metadata.summarized_fields || {};

          // Transform dataset to previewData array format
          const previewData = transformDatasetToPreviewData(widgetConfig.dataset);
          console.log('DashboardCanvas - Transformed previewData for updated widget:', previewData);

          // Use the transformed data
          setWidgetChartData(prev => ({
            ...prev,
            [widgetConfig.id]: {
              previewData: previewData,
              summarizedData: widgetConfig.dataset,
              groupingInfo: null,
              selectedFields: Object.keys(summarizedFields),
              summarizedFields: summarizedFields,
              selectedGroups: Object.keys(groupFields)
            }
          }));

          // Update the widget to have consistent summarizedData while preserving original dataset
          setWidgets(prev => prev.map(w =>
            w.id === widgetConfig.id ? { ...w, summarizedData: widgetConfig.dataset, dataset: widgetConfig.dataset } : w
          ));
        } else {
          // Load chart data for the widget from API only if no existing data
          console.log('DashboardCanvas - Loading chart data from API for widget:', widgetConfig.id);
          console.log('DashboardCanvas - Widget config for API load:', {
            id: widgetConfig.id,
            name: widgetConfig.name,
            parent_id: widgetConfig.parent_id,
            chart_type: widgetConfig.chart_type,
            hasPreviewData: !!widgetConfig.previewData,
            hasSummarizedData: !!widgetConfig.summarizedData,
            hasGroupingInfo: !!widgetConfig.groupingInfo
          });

          // Ensure we load chart data for the updated widget
          loadChartDataForWidget(widgetConfig);
        }
      }
    }
  };

  const handleRemoveWidget = (widgetId) => {
    console.log('DashboardCanvas - Removing widget:', widgetId);

    // Remove widget from UI state
    setWidgets(prev => prev.filter(widget => widget.id !== widgetId));

    // Remove chart data for the widget
    setWidgetChartData(prev => {
      const newData = { ...prev };
      delete newData[widgetId];
      return newData;
    });
  };

  const handleEditWidget = (widget) => {
    console.log('DashboardCanvas - Editing widget:', widget);

    // Open WidgetBuilder in edit mode
    setShowSidebar(true);
    setEditingWidget(widget);
  };

  const handleOpenWidgetFilters = async (widget) => {
    const parentId = widget.parent_id || widget.report_id;
    if (!parentId) return;

    setFilterModalWidgetId(widget.id);
    setFilterMetaLoading(true);
    try {
      let meta = widgetFilterMeta[widget.id];
      if (!meta) {
        meta = await fetchReportFilterMeta(parentId);
        setWidgetFilterMeta((prev) => ({ ...prev, [widget.id]: meta }));
      }

      const applied = widgetRuntimeFilters[widget.id];
      const baseFilters = meta.reportFilters || [];
      const draft = applied?.length
        ? baseFilters.map((rf) => {
          const match = applied.find(
            (a) => String(a.field_config_id) === String(rf.field_config_id)
          );
          return match
            ? {
                ...rf,
                operator: match.operator,
                value: match.value,
                valueType: getFilterValueType(match),
              }
            : { ...rf };
        })
        : baseFilters.map((f) => ({ ...f }));

      setWidgetFilterDraft((prev) => ({ ...prev, [widget.id]: draft }));
    } catch (err) {
      console.error('DashboardCanvas - Failed to load filter metadata:', err);
      setError('Failed to load report filters. Please try again.');
      setFilterModalWidgetId(null);
    } finally {
      setFilterMetaLoading(false);
    }
  };

  const handleWidgetFilterDraftChange = (widgetId) => (filterIndex) => (idx, key, val) => {
    if (idx !== filterIndex) return;
    const availableFields =
      widgetFilterMeta[widgetId]?.filterAvailableFields || [];

    setWidgetFilterDraft((prev) => {
      const draft = prev[widgetId] || [];
      const updated = applyReportFilterConditionChange(
        draft,
        filterIndex,
        key,
        val,
        availableFields
      );
      return { ...prev, [widgetId]: updated };
    });
  };

  const handleApplyWidgetFilters = async () => {
    const widgetId = filterModalWidgetId;
    if (!widgetId) return;

    const widget = widgets.find((w) => w.id === widgetId);
    if (!widget) return;

    const draft = widgetFilterDraft[widgetId] || [];
    const filtersArray = draft
      .filter((f) => f.field_name && f.field_config_id)
      .map((f, index) => normalizeReportFilterForApi(f, index));

    setWidgetRuntimeFilters((prev) => ({ ...prev, [widgetId]: filtersArray }));
    setFilterModalWidgetId(null);

    const widgetType = (widget.type || widget.chart_type || '').toLowerCase();
    if (widgetType === 'kpi') {
      await loadKpiDataForWidget(widget, {
        filters: filtersArray,
        persistToBackend: false,
        forceRefresh: true,
      });
    } else {
      await loadChartDataForWidget(widget, {
        filters: filtersArray,
        persistToBackend: false,
      });
    }
  };

  const handleResetWidgetFilters = async () => {
    const widgetId = filterModalWidgetId;
    if (!widgetId) return;

    const widget = widgets.find((w) => w.id === widgetId);
    if (!widget) return;

    const meta = widgetFilterMeta[widgetId];
    const restored = (meta?.originalFilters || meta?.reportFilters || []).map(
      (f) => ({ ...f })
    );

    setWidgetFilterDraft((prev) => ({ ...prev, [widgetId]: restored }));
    setWidgetRuntimeFilters((prev) => {
      const next = { ...prev };
      delete next[widgetId];
      return next;
    });
    setFilterModalWidgetId(null);

    const widgetType = (widget.type || widget.chart_type || '').toLowerCase();
    if (widgetType === 'kpi') {
      const cached = widget.dataset;
      const canUseCached =
        cached &&
        isKpiDatasetValid(cached) &&
        !cached?._metadata?._runtime_filtered;
      if (canUseCached) {
        setWidgets((prev) =>
          prev.map((w) => (w.id === widgetId ? { ...w, dataset: cached } : w))
        );
      } else {
        await loadKpiDataForWidget(widget, {
          filters: [],
          persistToBackend: false,
          forceRefresh: true,
        });
      }
    } else if (widget.dataset && isDatasetStructureValid(widget.dataset)) {
      await processCachedDataset(widget);
    } else {
      await loadChartDataForWidget(widget, { filters: [], persistToBackend: true });
    }
  };

  const handleViewWidget = (widget) => {
    console.log('DashboardCanvas - Viewing widget:', widget);
    setViewingWidget(widget);
  };

  const handleCloseViewModal = () => {
    setViewingWidget(null);
  };

  const renderRunAsInfoButton = (runAsLabel) => {
    if (!runAsLabel || !String(runAsLabel).trim()) {
      return null;
    }
    const parts = String(runAsLabel)
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean);
    const name = parts[0] || '—';
    const username = parts[1] || '—';
    const role = parts.length > 2 ? parts.slice(2).join(' | ') : '—';

    return (
      <div
        className="widget-runas-info"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="widget-action-btn widget-info-btn"
          aria-label="Run as user information"
          title="View as"
        >
          <FaInfoCircle />
        </button>
        <div className="widget-runas-popover" role="tooltip">
          <div className="widget-runas-popover-heading">View as</div>
          <div className="widget-runas-popover-row">
            <span className="widget-runas-popover-key">Name</span>
            <span className="widget-runas-popover-val">{name}</span>
          </div>
          <div className="widget-runas-popover-row">
            <span className="widget-runas-popover-key">User</span>
            <span className="widget-runas-popover-val">{username}</span>
          </div>
          <div className="widget-runas-popover-row">
            <span className="widget-runas-popover-key">Role</span>
            <span className="widget-runas-popover-val">{role}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderWidgetActionButtons = (
    widget,
    {
      supportsRuntimeFilters,
      hasActiveRuntimeFilters,
      supportsRefresh,
      parentId,
      isDashboardWidget,
      showReportLink = false,
      showDashboardLink = false,
      runAsLabel = '',
      showExpand = true,
      isKpiWidget = false,
    }
  ) => {
    if (isKpiWidget && isRunMode) {
      return (
        <>
          {supportsRuntimeFilters && (
            <button
              type="button"
              className={`widget-action-btn widget-filter-btn${hasActiveRuntimeFilters ? ' widget-filter-btn-active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenWidgetFilters(widget);
              }}
              title={
                hasActiveRuntimeFilters ? 'Edit active filters' : 'Filter widget data'
              }
              disabled={loadingWidgets[widget.id] || filterMetaLoading}
            >
              <FaFilter />
            </button>
          )}
          {supportsRefresh && (
            <button
              type="button"
              className="widget-action-btn widget-refresh-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleRefreshWidget(widget);
              }}
              title={
                hasActiveRuntimeFilters
                  ? 'Clear filters and restore cached data'
                  : 'Refresh widget data'
              }
              disabled={loadingWidgets[widget.id]}
            >
              <FaSync className={loadingWidgets[widget.id] ? 'spinning' : ''} />
            </button>
          )}
        </>
      );
    }

    return (
      <>
        {renderRunAsInfoButton(runAsLabel)}
        {showExpand && (
          <button
            type="button"
            className="widget-action-btn widget-expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleViewWidget(widget);
            }}
            title="View full screen"
          >
            <FaExpand />
          </button>
        )}
        {supportsRuntimeFilters && (
          <button
            type="button"
            className={`widget-action-btn widget-filter-btn${hasActiveRuntimeFilters ? ' widget-filter-btn-active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenWidgetFilters(widget);
            }}
            title={
              hasActiveRuntimeFilters ? 'Edit active filters' : 'Filter widget data'
            }
            disabled={loadingWidgets[widget.id] || filterMetaLoading}
          >
            <FaFilter />
          </button>
        )}
        {supportsRefresh && (
          <button
            type="button"
            className="widget-action-btn widget-refresh-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleRefreshWidget(widget);
            }}
            title={
              hasActiveRuntimeFilters
                ? 'Clear filters and restore cached data'
                : 'Refresh widget data'
            }
            disabled={loadingWidgets[widget.id]}
          >
            <FaSync className={loadingWidgets[widget.id] ? 'spinning' : ''} />
          </button>
        )}
        {showReportLink && parentId && !isDashboardWidget && (
          <button
            type="button"
            className="widget-action-btn widget-report-link-btn"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/viewreport/${parentId}?source=reports`, '_blank');
            }}
            title={`Report: ${widget.report_label || widget.name || 'Open report'}`}
          >
            <FaExternalLinkAlt />
          </button>
        )}
        {showDashboardLink && parentId && (
          <button
            type="button"
            className="widget-action-btn widget-dashboard-link-btn"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/dashboard/${parentId}/run`, '_blank');
            }}
            title={`Dashboard: ${widget.dashboard_label || widget.name || 'Open dashboard'}`}
          >
            <FaExternalLinkAlt />
          </button>
        )}
        {!isRunMode && !embedded && (
          <>
            <button
              type="button"
              className="widget-action-btn widget-edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleEditWidget(widget);
              }}
              title="Edit widget"
            >
              <FaEdit />
            </button>
            <button
              type="button"
              className="widget-action-btn widget-delete-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveWidget(widget.id);
              }}
              title="Remove widget"
            >
              <FaTrash />
            </button>
          </>
        )}
      </>
    );
  };

  const renderWidgetResizeHandles = (widget) => (
    <div className="widget-compact-resize-handles widget-resize-handles" role="presentation">
      <div
        className="resize-handle resize-se"
        data-handle="bottom-right"
        onMouseDown={(e) => {
          const handle = e.currentTarget.dataset.handle || 'bottom-right';
          handleMouseDown(e, widget, 'resize');
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        title="Resize width and height"
      >
        <FaExpand />
      </div>
      <div
        className="resize-handle resize-s"
        data-handle="bottom"
        onMouseDown={(e) => {
          handleMouseDown(e, widget, 'resize');
        }}
        title="Resize height"
      >
        <FaArrowsAlt />
      </div>
      <div
        className="resize-handle resize-e"
        data-handle="right"
        onMouseDown={(e) => {
          handleMouseDown(e, widget, 'resize');
        }}
        title="Resize width"
      >
        <FaArrowsAlt />
      </div>
    </div>
  );

  // Refresh widget data from API (clears run-time filters and restores cached dataset when possible)
  const handleRefreshWidget = async (widget) => {
    const normalizedChartType = (widget.chart_type || widget.type || '').toLowerCase();
    if (normalizedChartType === 'report') {
      console.log('DashboardCanvas - Refresh skipped for full report widget:', widget.id);
      return;
    }
    if (normalizedChartType === 'dashboard') {
      const embeddedParentId = widget.parent_id || widget.dashboard_id;
      if (!embeddedParentId) {
        return;
      }
      try {
        console.log('DashboardCanvas - Refreshing embedded dashboard widget:', widget.id);
        const embeddedRef = embeddedDashboardRefs.current[widget.id];
        if (embeddedRef?.refreshAllWidgets) {
          await embeddedRef.refreshAllWidgets();
        }
      } catch (err) {
        console.error('Error refreshing embedded dashboard widget:', err);
      }
      return;
    }
    try {
      console.log('DashboardCanvas - Refreshing widget data:', widget.id);

      setWidgetRuntimeFilters((prev) => {
        const next = { ...prev };
        delete next[widget.id];
        return next;
      });

      const currentWidget = widgets.find((w) => w.id === widget.id) || widget;

      if (normalizedChartType === 'kpi') {
        await loadKpiDataForWidget(currentWidget, { forceRefresh: true });
        return;
      }

      await loadChartDataForWidget(currentWidget, {
        filters: [],
        persistToBackend: true,
      });

      console.log('DashboardCanvas - Widget refreshed successfully:', widget.id);
    } catch (err) {
      console.error('Error refreshing widget:', err);
    }
  };

  const handleRefreshAllWidgets = useCallback(async () => {
    if (refreshingAll || getRefreshableWidgetsSorted(widgets).length === 0) {
      return;
    }

    setRefreshingAll(true);
    try {
      await refreshWidgetsSequentially(widgets, handleRefreshWidget);
    } catch (err) {
      console.error('Error refreshing all dashboard widgets:', err);
    } finally {
      setRefreshingAll(false);
    }
  }, [refreshingAll, widgets]);

  useImperativeHandle(
    isValidImperativeRef(ref) ? ref : null,
    () => ({
      refreshAllWidgets: handleRefreshAllWidgets,
    }),
    [handleRefreshAllWidgets]
  );

  const persistDashboardWidgets = async () => {
    if (saving) {
      return null;
    }

    setSaving(true);
    setError(null);

    try {
      // Detect deleted widgets (widgets that were in originalWidgets but not in current widgets)
      const currentWidgetIds = widgets.map(w => w.id).filter(id => id && !id.toString().startsWith('preview-'));
      const deletedWidgets = originalWidgets.filter(original =>
        original.id && !currentWidgetIds.includes(original.id)
      );

      console.log('DashboardCanvas - Widget deletion analysis:', {
        originalCount: originalWidgets.length,
        currentCount: widgets.length,
        deletedCount: deletedWidgets.length,
        deletedWidgets: deletedWidgets.map(w => ({ id: w.id, name: w.name }))
      });

      // Send ALL widgets (from database + newly created from WidgetBuilder)
      // Backend will handle CREATE vs UPDATE based on ID presence
      console.log('DashboardCanvas - Sending all widgets for upsert:', {
        total: widgets.length,
        new: widgets.filter(w => !w.id || w.id.toString().startsWith('preview-')).length,
        existing: widgets.filter(w => w.id && !w.id.toString().startsWith('preview-')).length,
        deleted: deletedWidgets.length
      });

      if (widgets.length === 0) {
        setError('No widgets to save');
        return null;
      }

      const dashboardApi = new DataApi('dashboard');

      // Prepare all widgets data for upsert
      const widgetsData = widgets.map(widget => {

        // Use summarizedData (now standardized), but handle empty dataset objects
        let dataset = widget.summarizedData || null;

        // If summarizedData is null but widget has dataset, use it (even if empty)
        if (!dataset && widget.dataset !== undefined) {
          dataset = widget.dataset;
        }

        // Blank ID for new widgets (preview-xxx IDs), keep ID for existing widgets
        const widgetId = (!widget.id || widget.id.toString().startsWith('preview-')) ? null : widget.id;

        console.log('DashboardCanvas - Widget dataset sources:', {
          widgetId: widgetId,
          widgetName: widget.name,
          hasSummarizedData: !!widget.summarizedData,
          hasDataset: widget.dataset !== undefined,
          datasetIsEmpty: widget.dataset && Object.keys(widget.dataset).length === 0,
          finalDataset: !!dataset,
          finalDatasetType: typeof dataset,
          finalDatasetKeys: dataset && typeof dataset === 'object' ? Object.keys(dataset) : 'N/A'
        });

        const normalizedChartType = (widget.chart_type || widget.type || (widget.chart_config && widget.chart_config.chartType) || '').toLowerCase();
        const chartTypeValue = widget.chart_type || (widget.chart_config && widget.chart_config.chartType) || 'chart';
        const chartConfigValue = normalizedChartType === 'report' ? null : (widget.chart_config || widget.selectedChart);
        const chartIdValue = normalizedChartType === 'report' ? null : widget.chart_id;
        const datasetValue = normalizedChartType === 'report' ? null : dataset;
        const parentIdValue = widget.parent_id || widget.report_id || null;

        console.log('DashboardCanvas - Preparing widget for upsert:', {
          id: widgetId, // null for CREATE, actual ID for UPDATE
          name: widget.name,
          parent_id: parentIdValue,
          chart_id: chartIdValue,
          chart_type: chartTypeValue,
          chart_config: chartConfigValue,
          column_span: widget.column_span,
          row_span: widget.row_span,
          sort_order: widget.sort_order || widgets.indexOf(widget),
          dataset: datasetValue
        });

        const widgetTypeValue =
          normalizedChartType === 'report'
            ? 'report'
            : normalizedChartType === 'dashboard'
              ? 'dashboard'
              : normalizedChartType === 'kpi'
                ? 'KPI'
                : 'chart';

        const payload = {
          id: widgetId, // null for CREATE, actual ID for UPDATE
          name: widget.name,
          type: widgetTypeValue,
          parent_id: parentIdValue,
          chart_id: chartIdValue,
          chart_config: chartConfigValue,
          column_span: widget.column_span,
          row_span: widget.row_span,
          sort_order: widget.sort_order || widgets.indexOf(widget),
          dataset: datasetValue,
          refresh_datetime: widget.refresh_datetime || null,
        };

        const refreshRateSeconds = getWidgetRefreshRateSeconds(widget);

        if (widgetTypeValue === 'KPI' && widget.widget_config) {
          const kpiConfig = normalizeKpiWidgetConfig(widget.widget_config);
          const displayLabel = getKpiWidgetLabel(widget);
          if (displayLabel) {
            kpiConfig.label = displayLabel;
          }
          kpiConfig.refresh_rate = refreshRateSeconds;
          payload.widget_config = kpiConfig;
          if (displayLabel) {
            payload.name = displayLabel;
          }
        } else if (chartConfigValue && typeof chartConfigValue === 'object') {
          payload.chart_config = {
            ...chartConfigValue,
            refresh_rate: refreshRateSeconds,
          };
        }

        return payload;
      });

      console.log('DashboardCanvas - Sending widgets for upsert:', {
        count: widgetsData.length,
        new: widgetsData.filter(w => w.id === null).length,
        existing: widgetsData.filter(w => w.id !== null).length
      });

      /*let executesave  = false;
      const response = null;
      if(executesave === true){
        // Send all widgets in single batch - backend handles upsert logic
        response = await dashboardApi.post(`/${dashboardId}/widgets/upsert`, {
          widgets: widgetsData
        });
      }*/

      const response = await dashboardApi.post(`/${dashboardId}/widgets/upsert`, {
        widgets: widgetsData,
        deletedWidgets: deletedWidgets.map(w => ({ id: w.id, name: w.name })) // Send deleted widget IDs
      });

      if (!response.data.success) {
        throw new Error(`Failed to save widgets: ${response.data.message || 'Unknown error'}`);
      }

      const savedWidgets = response.data.data;
      const newWidgetsInOrder = widgets.filter(
        (w) => !w.id || String(w.id).startsWith('preview-')
      );
      const newlyCreatedSaved = savedWidgets.filter((s) => s.original_id == null);

      const updatedWidgets = widgets.map((widget) => {
        const isNew =
          !widget.id || String(widget.id).startsWith('preview-');

        if (isNew) {
          const pos = newWidgetsInOrder.indexOf(widget);
          const savedWidget = newlyCreatedSaved[pos];
          if (savedWidget?.id) {
            return { ...widget, id: savedWidget.id, isPreview: false };
          }
          return widget;
        }

        const savedWidget = savedWidgets.find(
          (saved) =>
            saved.original_id === widget.id || saved.id === widget.id
        );
        if (savedWidget?.id) {
          return { ...widget, id: savedWidget.id, isPreview: false };
        }
        return widget;
      });

      setWidgets(updatedWidgets);
      setOriginalWidgets(updatedWidgets);

      const deletedCount = deletedWidgets.length;
      const savedCount = savedWidgets.length;
      const message = deletedCount > 0
        ? `Saved ${savedCount} widget${savedCount === 1 ? '' : 's'} and deleted ${deletedCount}.`
        : `Saved ${savedCount} widget${savedCount === 1 ? '' : 's'}.`;

      return { updatedWidgets, message, deletedCount, savedCount };
    } catch (err) {
      console.error('Error saving dashboard:', err);
      setError('Error saving dashboard. Please try again.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleQuickSaveDashboard = async () => {
    await persistDashboardWidgets();
  };

  const handleSaveAndExitDashboard = async () => {
    const result = await persistDashboardWidgets();
    if (result) {
      navigate('/dashboards');
    }
  };


  const getChartTypeLabel = (chartType) => getChartTypeDisplayLabel(chartType);

  const transformChartForDisplay = (widget) => {
    const slot = widgetChartData[widget.id];
    const summarizedData =
      slot?.summarizedData || widget.dataset || widget.summarizedData || null;
    const chartConfig = slot?.chartConfig || widget.chart_config;
    return transformWidgetChartForDisplay(
      { ...widget, chart_config: chartConfig },
      summarizedData
    );
  };

  const runUserDisplayName = runUserDisplayLabel
    ? runUserDisplayLabel.split('|')[0].trim()
    : '';


  if (loading) {
    return (
      <div className={`dashboard-canvas-main ${embedded ? 'dashboard-canvas-embedded' : ''}`}>
        <div className="canvas-loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-canvas-main ${embedded ? 'dashboard-canvas-embedded' : ''}`}>
      {/* Header */}
      {showHeader && (
        <div
          className="canvas-header"
          style={embedded ? { zIndex: 1 } : undefined}
        >
          <div className="canvas-header-content">
            <div className="canvas-header-left">
              {!embedded && (
                <button
                  type="button"
                  className="canvas-back-btn"
                  onClick={() => navigate('/dashboards')}
                >
                  <FaArrowLeft aria-hidden />
                  Back to dashboards
                </button>
              )}
              <div className="canvas-title">
                <h1>
                  {loading && !dashboard ? (
                    <span className="loading-text">Loading...</span>
                  ) : (
                    dashboard?.label || dashboard?.name || 'Dashboard Canvas'
                  )}
                </h1>
                <p className="canvas-description">
                  {loading && !dashboard ? (
                    <span className="loading-text">Loading dashboard details...</span>
                  ) : (
                    <>
                      {dashboard?.description ? (
                        <span className="dashboard-description">{dashboard.description}</span>
                      ) : (
                        <span>{isRunMode ? 'Dashboard View' : 'Manage and view your dashboard widgets'}</span>
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>
            {(isRunMode ? widgets.length > 0 : true) && (
            <div className="canvas-actions">
              {isRunMode ? (
                <button
                  type="button"
                  className="canvas-action-btn canvas-refresh-all-btn"
                  title="Refresh all widgets"
                  onClick={handleRefreshAllWidgets}
                  disabled={refreshingAll}
                >
                  <FaSync className={refreshingAll ? 'spinning' : ''} />
                  {refreshingAll ? 'Refreshing...' : 'Refresh all'}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="canvas-action-btn canvas-quick-save-btn"
                    onClick={handleQuickSaveDashboard}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Quick Save'}
                  </button>
                  <button
                    type="button"
                    className="canvas-action-btn canvas-save-btn"
                    onClick={handleSaveAndExitDashboard}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    className="canvas-action-btn canvas-add-btn"
                    onClick={() => setShowSidebar(true)}
                  >
                    <FaPlus />
                    Add Widget
                  </button>
                  <button
                    type="button"
                    className="canvas-action-btn canvas-settings-btn"
                    title="Dashboard settings"
                    onClick={() => setShowDashboardSettings(true)}
                  >
                    <FaCog />
                  </button>
                </>
              )}
            </div>
            )}
          </div>
        </div>
      )}

      {!isRunMode && (
        <DashboardSettingsModal
          show={showDashboardSettings}
          dashboard={dashboard}
          onClose={() => setShowDashboardSettings(false)}
          onSaved={() => loadDashboardAndWidgets()}
        />
      )}

      {/* Error Message */}
      {error && (
        <div className="canvas-error">
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Canvas Grid */}
      <div className="canvas-grid-container">
        {widgets.length === 0 ? (
          <div className="canvas-empty-state">
            <div className="empty-state-content">
              <div className="empty-icon">
                <FaPlus />
              </div>
              <h3>No widgets yet</h3>
              <p>{isRunMode ? 'This dashboard has no widgets to display' : 'Start building your dashboard by adding widgets'}</p>
              {!isRunMode && (
                <button
                  className="canvas-add-btn"
                  onClick={() => setShowSidebar(true)}
                >
                  <FaPlus />
                  Add Your First Widget
                </button>
              )}
            </div>
          </div>
        ) : (
          <div
            className={`canvas-grid ${isRunMode ? 'run-mode' : ''}${isResizing ? ' canvas-resizing' : ''}${refreshingAll ? ' canvas-refreshing-all' : ''}`}
            ref={canvasRef}
          >
            {widgets
              .sort((a, b) => {
                // Handle widgets without sort_order (put them at the end)
                const aOrder = a.sort_order || 999999;
                const bOrder = b.sort_order || 999999;

                // Sort by sort_order (ascending)
                return aOrder - bOrder;
              })
              .map((widget, index) => {
                const normalizedChartType = (widget.chart_type || widget.type || '').toLowerCase();
                const isDashboardWidget = normalizedChartType === 'dashboard';
                const isReportWidget = normalizedChartType === 'report';
                const isKpiWidget = normalizedChartType === 'kpi';
                const supportsRefresh = !isReportWidget;
                const supportsRuntimeFilters = isRunMode && supportsRefresh;
                const hasActiveRuntimeFilters =
                  (widgetRuntimeFilters[widget.id] || []).length > 0;
                const parentId = widget.parent_id || widget.report_id || null;
                const columnSpan = widget.column_span || 6;
                const rowSpan = widget.row_span || 4;
                const isCompactKpi = isKpiWidget && rowSpan <= 2;
                const isCompactWidget = rowSpan <= 2 || columnSpan <= 3;
                const showReportLink = !isDashboardWidget && !!parentId;
                const showDashboardLink = isDashboardWidget && !!parentId;
                const toolbarLabel = isKpiWidget
                  ? getKpiWidgetLabel(widget) || widget.name || 'KPI'
                  : widget.name || 'Widget';
                console.log(`DashboardCanvas - Rendering widget ${widget.id} (position ${index + 1}) with sort_order: ${widget.sort_order || 'none'}, column_span: ${columnSpan}, row_span: ${rowSpan}`);
                console.log(`DashboardCanvas - Widget ${widget.id} grid styles:`, {
                  gridColumn: `span ${columnSpan}`,
                  gridRow: `span ${rowSpan}`,
                  gridColumnStart: widget.x || 'auto',
                  gridRowStart: widget.y || 'auto'
                });
                console.log(`DashboardCanvas - Widget ${widget.id} raw data:`, widget);

                const chromeVisible = widgetChrome.isChromeVisible(widget.id);

                return (
                  <div
                    key={widget.id}
                    className={`canvas-widget widget-compact-layout widget-${normalizedChartType || 'chart'}${isKpiWidget ? ' widget-kpi' : ''}${isCompactKpi ? ' widget-kpi-compact' : ''}${isCompactWidget ? ' widget-compact-size' : ''} ${isRunMode ? 'run-mode-widget' : ''} ${chromeVisible ? 'widget-chrome-visible' : ''} ${isResizing && draggedWidget?.id === widget.id ? 'resizing' : ''}`}
                    data-widget-chrome-id={isRunMode ? widget.id : undefined}
                    tabIndex={isRunMode ? 0 : undefined}
                    onBlur={isRunMode ? widgetChrome.handleWidgetBlur(widget.id) : undefined}
                    onKeyDown={isRunMode ? widgetChrome.handleWidgetKeyDown : undefined}
                    style={{
                      gridColumn: `span ${columnSpan}`,
                      gridRow: `span ${rowSpan}`,
                      gridColumnStart: widget.x || 'auto',
                      gridRowStart: widget.y || 'auto',
                      '--column-span': columnSpan,
                      '--row-span': rowSpan
                    }}
                    data-column-span={columnSpan}
                    data-row-span={rowSpan}

                  >
                    <div
                      className={`widget-compact-chrome${isRunMode && !chromeVisible ? ' widget-compact-chrome-hidden' : ''}`}
                      style={isDashboardWidget ? { zIndex: 101 } : undefined}
                      onClick={(e) => e.stopPropagation()}
                      role="toolbar"
                      aria-label={`Actions for ${toolbarLabel}`}
                    >
                      {widget.isPreview && (
                        <span className="widget-compact-preview-badge">Preview</span>
                      )}
                      <div className="widget-compact-actions">
                        {renderWidgetActionButtons(widget, {
                          supportsRuntimeFilters,
                          hasActiveRuntimeFilters,
                          supportsRefresh,
                          parentId,
                          isDashboardWidget,
                          showReportLink,
                          showDashboardLink,
                          runAsLabel: runUserDisplayLabel,
                          showExpand: !isKpiWidget,
                          isKpiWidget,
                        })}
                      </div>
                    </div>

                    {!isKpiWidget && widget.name && (
                      <div className="widget-compact-title" title={widget.name}>
                        {widget.name}
                      </div>
                    )}

                    {/* Widget Content — compact toolbar overlays all widget types */}
                    <div
                      className="widget-content"
                      onClick={isRunMode ? widgetChrome.handleContentClick(widget.id) : undefined}
                      role={isRunMode ? 'button' : undefined}
                      tabIndex={isRunMode ? -1 : undefined}
                      aria-label={isRunMode ? `Show controls for ${toolbarLabel}` : undefined}
                    >
                      <div className="widget-chart-container">
                        {isDashboardWidget && parentId ? (
                          <EmbeddedDashboardViewer
                            widgetId={widget.id}
                            dashboardId={parentId}
                            showHeader={widget.show_header !== undefined ? widget.show_header : true}
                            onRegisterRef={registerEmbeddedDashboardRef}
                          />
                        ) : isReportWidget && parentId ? (
                          <div className="widget-report-viewer">
                            <ReportViewer
                              embed={true}
                              reportId={parentId}
                              onError={(message) => {
                                console.error('DashboardCanvas - ReportViewer error:', message);
                              }}
                            />
                          </div>
                        ) : isKpiWidget ? (
                          <KpiWidget
                            title={getKpiWidgetLabel(widget) || null}
                            dataset={widget.dataset}
                            loading={!!loadingWidgets[widget.id]}
                            error={widgetKpiErrors[widget.id]}
                            compact={isCompactKpi}
                            widgetConfig={normalizeKpiWidgetConfig(widget.widget_config)}
                          />
                        ) : widgetChartData[widget.id] ? (
                          <div
                            className={`widget-chart${loadingWidgets[widget.id] ? ' widget-chart-refreshing' : ''}`}
                          >
                            {loadingWidgets[widget.id] ? (
                              <div
                                className="widget-refresh-overlay"
                                aria-busy="true"
                                aria-label="Refreshing chart"
                              >
                                <div className="loading-spinner"></div>
                              </div>
                            ) : null}
                            {console.log('DashboardCanvas - ChartDisplay props for widget:', widget.id, {
                              selectedFields: widgetChartData[widget.id].selectedFields,
                              summarizedFields: widgetChartData[widget.id].summarizedFields,
                              selectedGroups: widgetChartData[widget.id].selectedGroups,
                              summarizedData: widgetChartData[widget.id].summarizedData,
                              previewData: widgetChartData[widget.id].previewData
                            })}
                            <ChartRenderer
                              chart={transformChartForDisplay(widget)}
                              selectedFields={widgetChartData[widget.id].selectedFields || []}
                              summarizedFields={widgetChartData[widget.id].summarizedFields || {}}
                              selectedGroups={widgetChartData[widget.id].selectedGroups || []}
                              selectedColumnGroups={widgetChartData[widget.id].selectedColumnGroups || []}
                              summarizedData={widgetChartData[widget.id].summarizedData}
                              previewData={widgetChartData[widget.id].previewData}
                              baseCurrencyCode={reportConversionBaseCode}
                              exchangeRateLookup={reportConversionLookup}
                              multiCurrencyEnabled={multiCurrencyEnabled}
                              isPreview={true}
                              fillContainer={true}
                              compactLayout={isCompactWidget}
                              displayAuditInfo={false}
                            />
                          </div>
                        ) : loadingWidgets[widget.id] ? (
                          <div className="widget-loading">
                            <div className="loading-spinner"></div>
                            <p>Loading chart...</p>
                          </div>
                        ) : (
                          <div className="widget-chart">
                            <div className="chart-container">
                              {widget.name ? (
                                <div className="chart-title">{widget.name}</div>
                              ) : null}
                              <div
                                className="chart-placeholder"
                                style={{ textAlign: 'center', color: '#6c757d', padding: '1rem' }}
                              >
                                No data available
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {!isRunMode && renderWidgetResizeHandles(widget)}
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Widget Builder */}
      {!isRunMode && (
        <WidgetBuilder
          isOpen={showSidebar}
          onClose={() => {
            setShowSidebar(false);
            setEditingWidget(null);
          }}
          dashboardId={dashboardId}
          onAddWidget={handleAddWidget}
          onPreviewWidget={handlePreviewWidget}
          editingWidget={editingWidget}
        />
      )}

      {/* Widget View Modal */}
      {viewingWidget && (
        <div className="widget-view-modal-overlay" onClick={handleCloseViewModal}>
          <div className="widget-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="widget-view-modal-header">
              <h3>{viewingWidget.name}</h3>
              <button className="widget-view-modal-close" onClick={handleCloseViewModal}>
                ×
              </button>
            </div>
            <div className="widget-view-modal-body">
              {(() => {
                const viewingNormalizedType = (viewingWidget.type || viewingWidget.chart_type || '').toLowerCase();
                const viewingParentId = viewingWidget.parent_id || viewingWidget.report_id || null;

                if (viewingNormalizedType === 'dashboard' && viewingWidget.parent_id) {
                  return (
                    <DashboardCanvas
                      isRunMode={true}
                      dashboardId={viewingWidget.parent_id}
                      showHeader={viewingWidget.show_header !== undefined ? viewingWidget.show_header : true}
                      embedded={true}
                    />
                  );
                }

                if (viewingNormalizedType === 'report' && viewingParentId) {
                  return (
                    <ReportViewer
                      embed={true}
                      reportId={viewingParentId}
                      onError={(message) => {
                        console.error('DashboardCanvas - ReportViewer modal error:', message);
                      }}
                    />
                  );
                }

                if (viewingNormalizedType === 'kpi') {
                  return (
                    <KpiWidget
                      title={getKpiWidgetLabel(viewingWidget) || viewingWidget.name}
                      dataset={viewingWidget.dataset}
                      loading={!!loadingWidgets[viewingWidget.id]}
                      error={widgetKpiErrors[viewingWidget.id]}
                      widgetConfig={normalizeKpiWidgetConfig(viewingWidget.widget_config)}
                    />
                  );
                }

                if (widgetChartData[viewingWidget.id]) {
                  return (
                    <ChartRenderer
                      key={`zoom-${viewingWidget.id}`}
                      chart={transformChartForDisplay(viewingWidget)}
                      selectedFields={widgetChartData[viewingWidget.id].selectedFields || []}
                      summarizedFields={widgetChartData[viewingWidget.id].summarizedFields || {}}
                      selectedGroups={widgetChartData[viewingWidget.id].selectedGroups || []}
                      selectedColumnGroups={widgetChartData[viewingWidget.id].selectedColumnGroups || []}
                      summarizedData={widgetChartData[viewingWidget.id].summarizedData}
                      previewData={widgetChartData[viewingWidget.id].previewData}
                      baseCurrencyCode={reportConversionBaseCode}
                      exchangeRateLookup={reportConversionLookup}
                      multiCurrencyEnabled={multiCurrencyEnabled}
                      isPreview={true}
                      fillContainer={true}
                      displayAuditInfo={true}
                    />
                  );
                }

                return (
                  <div className="widget-view-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading widget data...</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <ReportRuntimeFilterModal
        show={!!filterModalWidgetId}
        onClose={() => setFilterModalWidgetId(null)}
        title={
          filterModalWidgetId
            ? `Filter: ${widgets.find((w) => w.id === filterModalWidgetId)?.name || 'Chart'}`
            : 'Manage Report Filters'
        }
        filterLogic={widgetFilterMeta[filterModalWidgetId]?.filter_logic}
        filterCustomLogic={widgetFilterMeta[filterModalWidgetId]?.filter_custom_logic}
        filterDraft={widgetFilterDraft[filterModalWidgetId] || []}
        filterAvailableFields={
          widgetFilterMeta[filterModalWidgetId]?.filterAvailableFields || []
        }
        onFilterDraftChange={
          filterModalWidgetId
            ? handleWidgetFilterDraftChange(filterModalWidgetId)
            : () => () => { }
        }
        onApply={handleApplyWidgetFilters}
        onReset={handleResetWidgetFilters}
        applyLabel="Apply Filters"
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setWidgetToDelete(null);
        }}
        onConfirm={confirmDeleteWidget}
        title="Confirm Delete"
        message="Are you sure you want to delete this widget from the dashboard?"
        itemName={widgetToDelete?.name}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
});

export default DashboardCanvas;
